import { EditEvaluation } from 'models/evaluation.model'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { defaultValues } from '../../constants'
import { IMainState } from 'store/states'
import { QuestionRating } from 'models/question.model'

import { ContextValue, SaveOptions, SaveQuestionsProviderProps } from './types'
import { useStep } from './hooks/useStep'
import * as fromActions from 'store/actions/blocks.action'
import { editQuestionsWeight } from 'api/administration/questions/Edit'

export const SaveQuestionsContext = createContext<ContextValue>(
  Object.create(null)
)

export const SaveQuestionsProvider = ({
  children,
  onClose,
}: SaveQuestionsProviderProps) => {
  const dispatch = useDispatch()
  const [data, setData] = useState<EditEvaluation>(defaultValues)
  const [editedQuestions, setQuestionsWeight] = useState<QuestionRating[]>([])

  const { edit } = useSelector(({ blocks }: IMainState) => blocks)
  const { hasResponses } = edit

  const save = useCallback(
    async (options?: SaveOptions) => {
      const { id, lessonId } = edit
      if (editedQuestions.length) await editQuestionsWeight(editedQuestions)

      dispatch({
        type: fromActions.EDIT_EVALUATION_BLOCK,
        payload: {
          ...data,
          id,
          lessonId,
          uploadVersion: options?.uploadVersion,
        },
      })
    },
    [data, dispatch, edit, editedQuestions]
  )

  const { step, onSaveAndChangeStep, setChanges, hasChanges } = useStep({
    data,
    editedQuestions,
    onClose,
    save,
  })

  const canShowConfirmation = useCallback(
    (opts?: { validAll?: boolean | undefined }) =>
      !!(hasResponses && hasChanges(opts)),
    [hasChanges, hasResponses]
  )

  useEffect(() => {
    if (edit?.evaluationConfig) {
      const {
        name,
        description,
        evaluationConfig: {
          approvalPoints,
          maxRetries,
          showResults,
          randomQuestions,
        },
      } = edit
      const aux: EditEvaluation = {
        details: { name: name, description: description },
        config: {
          approvalPoints: approvalPoints,
          maxRetries: maxRetries,
          showResults: showResults,
          randomQuestions: randomQuestions,
          editedQuestions: [],
        },
      }
      setData({ ...defaultValues, ...aux })
    }
  }, [edit, setData])

  return (
    <SaveQuestionsContext.Provider
      value={{
        data,
        setData,
        editedQuestions,
        setQuestionsWeight,
        step,
        setChanges,
        onSaveAndChangeStep,
        save,
        canShowConfirmation,
      }}
    >
      {children}
    </SaveQuestionsContext.Provider>
  )
}

export const useSaveQuestions = () => useContext(SaveQuestionsContext)
